import { db } from './micuDatabase';

const PAT_KEY = 'micu_pat';
const PAT_EXPIRY_KEY = 'micu_pat_expiry';
const PAT_USER_KEY = 'micu_pat_user';
const GRACE_DAYS = 14;

export async function getStoredPAT() {
  const record = await db.auth.where('key').equals(PAT_KEY).first();
  return record?.value || null;
}

export async function getStoredExpiry() {
  const record = await db.auth.where('key').equals(PAT_EXPIRY_KEY).first();
  return record?.value || null;
}

export async function getStoredUser() {
  const record = await db.auth.where('key').equals(PAT_USER_KEY).first();
  try {
    return record?.value ? JSON.parse(record.value) : null;
  } catch {
    return null;
  }
}

async function setAuthValue(key, value) {
  const existing = await db.auth.where('key').equals(key).first();
  if (existing) {
    await db.auth.update(existing.id, { value });
  } else {
    await db.auth.add({ key, value });
  }
}

export async function storePAT(token, expiresAt, user) {
  await setAuthValue(PAT_KEY, token);
  await setAuthValue(PAT_EXPIRY_KEY, expiresAt);
  await setAuthValue(PAT_USER_KEY, JSON.stringify(user));
}

export async function clearPAT() {
  await db.auth.where('key').anyOf([PAT_KEY, PAT_EXPIRY_KEY, PAT_USER_KEY]).delete();
}

export async function isPATExpiredLocally() {
  const expiresAt = await getStoredExpiry();
  if (!expiresAt) return true;
  return new Date(expiresAt) < new Date();
}

export async function isPATWithinGracePeriod() {
  const expiresAt = await getStoredExpiry();
  if (!expiresAt) return false;
  const expiry = new Date(expiresAt);
  const graceEnd = new Date(expiry.getTime() + GRACE_DAYS * 24 * 60 * 60 * 1000);
  return new Date() < graceEnd;
}

export async function validatePATWithServer() {
  const pat = await getStoredPAT();
  if (!pat) return { valid: false, reason: 'no_token' };

  try {
    const response = await fetch('/micu/validate-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-MICU-Token': pat,
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (data.valid) {
        await setAuthValue(PAT_EXPIRY_KEY, data.expiresAt);
        if (data.user) {
          await setAuthValue(PAT_USER_KEY, JSON.stringify(data.user));
        }
        return { valid: true, user: data.user, daysRemaining: data.daysRemaining };
      }
      return { valid: false, reason: data.reason || 'invalid' };
    }
    return { valid: false, reason: 'server_error' };
  } catch {
    return { valid: false, reason: 'network_error' };
  }
}

// Main auth check: returns { authenticated, user, needsLogin, offlineOk }
export async function checkAuth() {
  const pat = await getStoredPAT();
  if (!pat) return { authenticated: false, needsLogin: true, offlineOk: false };

  const expired = await isPATExpiredLocally();

  if (navigator.onLine) {
    const result = await validatePATWithServer();
    if (result.valid) {
      return { authenticated: true, user: result.user, needsLogin: false, offlineOk: true };
    }
    // Server says invalid — check grace period for offline fallback
    if (!expired || await isPATWithinGracePeriod()) {
      const user = await getStoredUser();
      return { authenticated: true, user, needsLogin: false, offlineOk: true };
    }
    return { authenticated: false, needsLogin: true, offlineOk: false };
  }

  // Offline
  if (!expired) {
    const user = await getStoredUser();
    return { authenticated: true, user, needsLogin: false, offlineOk: true };
  }

  if (await isPATWithinGracePeriod()) {
    const user = await getStoredUser();
    return { authenticated: true, user, needsLogin: false, offlineOk: true };
  }

  return { authenticated: false, needsLogin: true, offlineOk: false };
}
